/**
 * useUserPreferences — reads and writes per-user KPI configuration.
 *
 * Persistence strategy (in priority order):
 *   1. API  (GET/PUT /api/user/preferences via UserAPI.ts) — source of truth in prod
 *   2. localStorage — used as an offline/dev fallback so saves survive without a backend
 *
 * Behaviour:
 *   - On mount: load from localStorage immediately (instant, no flash), then
 *     overwrite with API response when it arrives.
 *   - On save: write to localStorage first (always succeeds), then attempt the API.
 *     If the API fails the save still sticks locally; no revert.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../services/apiClient';

export const DEFAULT_KPI_SELECTION = [
  'staffing.coverage',
  'quality.task_completion',
  'access.same_day',
  'quality.open_compliance',
  'consumer.satisfaction',
  'finance.denials',
];
const LS_KEY = (sub) => `nn_user_prefs_${sub || 'guest'}`;

function loadFromStorage(sub) {
  try {
    const raw = localStorage.getItem(LS_KEY(sub));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(sub, prefs) {
  try {
    localStorage.setItem(LS_KEY(sub), JSON.stringify(prefs));
  } catch {
    // storage quota exceeded — ignore
  }
}

export function useUserPreferences() {
  const { user, accessToken } = useAuth();
  const sub = user?.attributes?.sub;

  const [prefs, setPrefs] = useState(() => {
    const stored = loadFromStorage(sub);
    return stored ?? { kpi_config: { selected: DEFAULT_KPI_SELECTION } };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Re-load from localStorage when the user identity becomes known
  useEffect(() => {
    if (!sub) { setLoading(false); return; }
    const stored = loadFromStorage(sub);
    if (stored) setPrefs(stored);
  }, [sub]);

  // Fetch from API; overrides localStorage if successful
  useEffect(() => {
    if (!sub || !accessToken) { setLoading(false); return; }

    let cancelled = false;
    fetch(`${API_BASE}/api/user/preferences`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!cancelled) {
          setPrefs(data);
          saveToStorage(sub, data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [sub, accessToken]);

  const savePreferences = async (newPrefs) => {
    const merged = { ...prefs, ...newPrefs };

    // Always persist locally first — this is what the UI reads from
    setPrefs(merged);
    saveToStorage(sub, merged);
    setError(null);

    // Best-effort API sync; failure does not revert the local state
    try {
      const res = await fetch(`${API_BASE}/api/user/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(newPrefs),
      });
      if (!res.ok) throw new Error('save failed');
      const saved = await res.json();
      setPrefs(saved);
      saveToStorage(sub, saved);
    } catch {
      // API unavailable — localStorage save above is sufficient
    }
  };

  return { prefs, loading, error, savePreferences };
}
